/**
 * Report API SDK
 *
 * Provides typed methods for the generic report API.
 * All field options are fetched from Frappe meta - nothing hardcoded.
 */

import { getERPClient } from "./client";

/**
 * Field information from doctype meta
 */
export interface ReportFieldInfo {
  fieldname: string;
  label: string;
  fieldtype: string;
}

/**
 * Report meta response - categorized fields for report building
 */
export interface ReportMeta {
  /** Fields that can be used for GROUP BY */
  groupable_fields: ReportFieldInfo[];
  /** Fields that support SUM/AVG (numeric only) */
  aggregatable_fields: ReportFieldInfo[];
  /** All non-layout fields (for COUNT) */
  countable_fields: ReportFieldInfo[];
}

/**
 * Aggregation function types
 */
export type AggregationType = "sum" | "count" | "avg";

/**
 * A single group in grouped report data
 */
export interface ReportGroup {
  /** The group value (or "(No Value)" for NULL/empty) */
  group_value: string | number | null;
  /** Count of items in this group */
  count: number;
  /** Dynamic aggregation columns: fieldname_aggtype (e.g., amount_sum) */
  [key: string]: unknown;
}

/**
 * Grouped report data response
 */
export interface GroupedReportData {
  mode: "grouped";
  /** Array of groups with aggregations */
  groups: ReportGroup[];
  /** Totals across all groups */
  totals: Record<string, number>;
  /** The fieldname used for grouping */
  group_by_field: string;
  /** The label of the group by field */
  group_by_label: string;
}

/**
 * Flat report data response (no grouping)
 */
export interface FlatReportData {
  mode: "flat";
  /** Raw data rows */
  data: Record<string, unknown>[];
  /** Total count of records */
  total: number;
}

/**
 * Union type for report data response
 */
export type ReportData = GroupedReportData | FlatReportData;

/**
 * Parameters for get_report_data API
 */
export interface ReportDataParams {
  doctype: string;
  fields?: string[];
  group_by?: string;
  aggregations?: Record<string, AggregationType>;
  filters?: unknown[];
  order_by?: string;
  limit_start?: number;
  limit_page_length?: number;
}

/**
 * Report API methods
 */
export const reports = {
  /**
   * Get report meta for a doctype - categorized fields for report building
   *
   * @param doctype - The DocType to get meta for
   * @returns Report meta with groupable, aggregatable, and countable fields
   */
  getMeta: async (doctype: string): Promise<ReportMeta> => {
    const client = getERPClient();
    const response = await client.call<ReportMeta>(
      "zaviago_backend.api.generic_report.get_report_meta",
      { doctype }
    );
    return response;
  },

  /**
   * Get report data with optional grouping and aggregation
   *
   * @param params - Report data parameters
   * @returns Grouped or flat report data
   */
  getData: async (params: ReportDataParams): Promise<ReportData> => {
    const client = getERPClient();
    const response = await client.call<ReportData>(
      "zaviago_backend.api.generic_report.get_report_data",
      {
        doctype: params.doctype,
        fields: params.fields ? JSON.stringify(params.fields) : undefined,
        group_by: params.group_by,
        aggregations: params.aggregations
          ? JSON.stringify(params.aggregations)
          : undefined,
        filters: params.filters ? JSON.stringify(params.filters) : undefined,
        order_by: params.order_by,
        limit_start: params.limit_start,
        limit_page_length: params.limit_page_length,
      }
    );
    return response;
  },
};
